import React, { useState, useCallback, useEffect, useRef } from "react";
import { Link, RouteComponentProps } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "react-toastify";

import { Label, Button, Popup } from "semantic-ui-react";
import TemplateTable from "../../components/TemplateTable";
import { ITableAppRender } from "../../components/TableApp";


import KitRepository, { IKitListModel } from "../../Repositories/KitRepository";
import { ISearch } from "../../components/Search";

import errors from "../../Utils/ExtractErrors";
import { ISearchResponse } from "../../interfaces";
import { IFields } from "../../components/Search/FilterBuilder";
import ModalYesNo from "../../components/ModalYesNo";
import { GetUser } from "../../services/auth";

interface ILocationState {
    month: number;
    status: string;
}

const ConferenceList: React.FC<RouteComponentProps<any, any, ILocationState>> = ({ location }) => {

    const [list, setList] = useState<IKitListModel[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const refSearch = useRef<ISearch>({ searchString: "", type: "simples" });
    const refRecordsByPage = useRef<number>(20);
    const refPageNumber = useRef<string | number | undefined>(1);
    const refTotalRecords = useRef<number>(0);
    const refTotalPages = useRef<number>(1);
    const { role } = GetUser();

    const onChangeRecords = (value: number) => {
        refRecordsByPage.current = value;
        load();
    }

    const onPageChange = (pageNumber: string | number | undefined) => {
        refPageNumber.current = pageNumber;
        load();
    }

    const setEstado = (estado: number, gerenteSetor: string) => {
        switch (estado) {
            case 1:
                return <Popup
                    trigger={<Label ribbon icon="user times" color="red" content="DEV" />}
                    content={`G. Setor: ${gerenteSetor !== null ? gerenteSetor : "Indefinido"}`}
                    wide
                />
            case 2:
                return <Popup
                    trigger={<Label ribbon icon="thumbs down" color="black" content="NRT" />}
                    content={`G. Setor: ${gerenteSetor !== null ? gerenteSetor : "Indefinido"}`}
                    wide
                />
        }
    }

    const setStatus = (status: number, gerenteSetor: string) => {
        switch (status) {
            case 0:
                return <Label ribbon icon="box" content="EST" />
            case 1:
                return <Popup
                    trigger={<Label ribbon icon="user" color="pink" content="GES" />}
                    content={`G. Setor: ${gerenteSetor !== null ? gerenteSetor : "Indefinido"}`}
                    wide
                />
            case 2:
                return <Popup
                    trigger={<Label ribbon icon="id badge" color="violet" content="REP" />}
                    content={`G. Setor: ${gerenteSetor !== null ? gerenteSetor : "Indefinido"}`}
                    wide
                />
            case 3:
                return <Popup
                    trigger={<Label ribbon icon="street view" color="teal" content="REV" />}
                    content={`G. Setor: ${gerenteSetor !== null ? gerenteSetor : "Indefinido"}`}
                    wide
                />
            case 4:
                return <Popup
                    trigger={<Label ribbon icon="stopwatch" color="blue" content="PRE" />}
                    content={`G. Setor: ${gerenteSetor !== null ? gerenteSetor : "Indefinido"}`}
                    wide
                />
            case 5:
                return <Popup
                    trigger={<Label ribbon icon="thumbs down outline" color="orange" content="CFE" />}
                    content={`G. Setor: ${gerenteSetor !== null ? gerenteSetor : "Indefinido"}`}
                    wide
                />
            case 6:
                return <Popup
                    trigger={<Label ribbon icon="thumbs up outline" color="purple" content="CGS" />}
                    content={`G. Setor: ${gerenteSetor !== null ? gerenteSetor : "Indefinido"}`}
                    wide
                />
            case 7:
                return <Popup
                    trigger={<Label ribbon icon="handshake" color="olive" content="CGR" />}
                    content={`G. Setor: ${gerenteSetor !== null ? gerenteSetor : "Indefinido"}`}
                    wide
                />
            case 8:
                return <Popup
                    trigger={<Label ribbon icon="check" color="green" content="FIN" />}
                    content={`G. Setor: ${gerenteSetor !== null ? gerenteSetor : "Indefinido"}`}
                    wide
                />
            default:
                return <Label ribbon icon="dont" color="grey" content="???" />
        }
    }

    const tableRender: ITableAppRender[] = [
        {
            header: "Status",
            content: (x: IKitListModel) => x.estado !== 0 ? setEstado(x.estado, x.gerenteSetor) : setStatus(x.status, x.gerenteSetor),
            textAlign: "left",
            width: 1
        },
        {
            header: "Número",
            content: (x: IKitListModel) => x.numero,
            textAlign: "center",
            width: 2
        },
        {
            header: "Representante",
            content: (x: IKitListModel) => x.representante,
            width: 4
        },
        {
            header: "Revendedor",
            content: (x: IKitListModel) => x.revendedor,
            width: 4
        },
        {
            header: "Acerto",
            content: (x: IKitListModel) => x.acerto !== null ? format(new Date(x.acerto), "dd/MM/yyyy") : "",
            textAlign: "center",
            width: 1
        },
    ];

    const load = useCallback(async () => {

        setLoading(true);
        try {
            let response: ISearchResponse<IKitListModel>;

            if (refSearch.current.type === "simples") {
                response = await KitRepository.ListToConference({
                    searchRequest: {
                        search: refSearch.current.searchString,
                        pageNumber: refPageNumber.current,
                        recordsByPage: refRecordsByPage.current
                    },
                    month: location.state.month,
                    status: location.state.status
                });
            } else {
                response = await KitRepository.ListToConference({
                    searchRequest: {
                        filter: refSearch.current.searchString,
                        pageNumber: refPageNumber.current,
                        recordsByPage: refRecordsByPage.current
                    },
                    month: location.state.month,
                    status: location.state.status
                });
            }

            refTotalRecords.current = response.totalRecords;
            refTotalPages.current = response.totalPages;

            setList(response.list);
        } catch (err) {
            if (err.response.status === 401) {
                window.location.href = "/";
            }

            const error = errors.getAllErrors(err.response.data);

            if (error !== undefined)
                toast(error, { position: "top-center", type: "error" });
        }
        setLoading(false);
    }, [location.state.status, location.state.month]);

    const onSearch = ({ searchString, type }: ISearch) => {
        refSearch.current.searchString = searchString;
        refSearch.current.type = type;

        load();
    }

    const date = new Date();
    const actualDay = date.getDate();

    const setShowButton = () => {
        if (actualDay >= 1 && actualDay <= 31) {
            return false;
        }
        if(role === "Operador"){
            return false;
        }
        else {
            return true;
        }
    }

    const conferenceButton = useCallback(({ id, gerenteSetor, representante, revendedor, acerto }: { id: string; gerenteSetor: string; representante: string; revendedor: string; acerto: string; }) => (
        <Button
            disabled={setShowButton()}
            key={`conferencekit_buttonconference_${id}`}
            as={Link}            
            icon="calendar check"
            color="blue"
            to={{
                pathname: `/conferencekit/${id}`,
                state: {
                    gerenteSetor,
                    representante,
                    revendedor,
                    acerto,
                    month: location.state.month,
                    status: location.state.status
                }
            }}
        />
    ), [location.state.month, location.state.status]);

    const handleNaoRetornou = async (id: string) => {
        if (id.length > 0) {
            try {
                const response = await KitRepository.DidntReturn(id);

                toast(response.message, { position: "top-center", type: "success" });
                load();
            } catch (err) {
                if (err.response.status === 401) {
                    window.location.href = "/";
                }

                const error = errors.getAllErrors(err.response.data);

                if (error !== undefined)
                    toast(error, { position: "top-center", type: "error" });
            }
        } else {
            toast("Erro ao tentar atualizar o kit", { position: "top-center", type: "error" });
        }
    }

    const naoRetornouButton = (id: string, numero: number) => (
        <ModalYesNo
            key={id}
            trigger={<Button icon="thumbs down" color="black" />}
            header="Atualizar kit"
            content={`Deseja realmente atualizar o kit nº${numero} para não retornou?`}
            onSuccess={() => handleNaoRetornou(id)}
        />
    )

    useEffect(() => { load() }, [load]);

    const fields: IFields[] = [
        { label: "G. Regional", name: "Pedido.GerenteRegional.Nome", type: "string", roles: ["Admin", "Operador"] },
        { label: "G. Setor", name: "Pedido.GerenteSetor.Nome", type: "string", roles: ["Admin", "Operador", "GerenteR"] },
        { label: "Representante", name: "Pedido.Representante.Nome", type: "string", roles: ["Admin", "Operador", "GerenteR", "GerenteS"] },
        { label: "Revendedor", name: "Revendedor.Nome", type: "string", roles: ["Admin", "Operador", "GerenteR", "GerenteS", "Representante"] },
        { label: "Acerto", name: "Pedido.Acerto", type: "date", roles: ["Admin", "Operador", "GerenteR", "GerenteS", "Representante"] },
        { label: "Número", name: "Numero", type: "number", roles: ["Admin", "Operador", "GerenteR", "GerenteS", "Representante"] }
    ];

    return (
        <TemplateTable
            title="Conferência de kits"
            icon="clipboard check"
            tableList={list}
            tableRender={tableRender}
            tableActions={(data: IKitListModel, index: number) => {
                return ([
                    naoRetornouButton(data.id || "", data.numero),
                    conferenceButton({
                        id: data.id || "",
                        gerenteSetor: data.gerenteSetor,
                        representante: data.representante,
                        revendedor: data.revendedor,
                        acerto: data.acerto
                    })])
            }}
            searchPlaceHolder="Número/Representante/Revendedor"
            onSearch={onSearch}
            filterFields={fields}
            tableLoading={loading}
            tableOnChangeRecords={onChangeRecords}
            tablePagination={{
                currentPage: refPageNumber.current,
                totalRecords: refTotalRecords.current,
                totalPages: refTotalPages.current,
                onPageChange: onPageChange
            }}
        />
    );
};

export default ConferenceList;
